import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';

/**
 * Which brand this page belongs to, and where that came from.
 *
 * The portal is one build for every brand. Historically the brand flavour was
 * always the first URL segment (`/<flavor>/menu`) and every service read it
 * from `window.location.pathname.split('/')[1]`. Brands can now also be
 * served on their own host (`bookings.primebarbers.co.uk/reservation/…`),
 * where the flavour is looked up from the host name instead and the URL has
 * no brand segment at all. This module is the single place that knows which
 * of the two the current page is:
 *
 *   1. path — the first segment is not one of the app's own pages
 *      (ROUTE_ROOTS), so it is a brand flavour. Always wins, which is what
 *      keeps every existing link, QR code, email and iframe embed working.
 *   2. host — `GET api/Brand/ResolveHost?host=<hostname>` (matched against
 *      BRAND.TenantUrl on the API; the answer is cached in localStorage).
 *   3. referrer — inside an iframe, the embedding page's host, the same way.
 *
 * Resolved once, before the router starts (APP_INITIALIZER in AppModule).
 * Everything else reads the module-level helpers below instead of parsing
 * `location.pathname`: `brandFlavor()`, `pageSegment()`, `pagePath()`,
 * `pageOf(url)` and `brandUrl(page)`. Never read `location.pathname[1]` for
 * the brand or `[2]` for the page again — with the brand resolved from the
 * host the page is segment 1.
 */

/**
 * First URL segment of every top-level route in app-routing.module.ts.
 * A first segment that is NOT in this list is a brand flavour. The route
 * table asserts against this list at boot (assertRouteRootsKnown), so a
 * new page has to be added here — and a brand must never be given one of
 * these names as its flavour (the API should refuse them).
 */
export const ROUTE_ROOTS: ReadonlyArray<string> = [
  'home',
  'products',
  'events',
  'login',
  'menu',
  'app-menu',
  'new-menu',
  'app-new-menu',
  'dine-in',
  'new-dine-in',
  'join-reward',
  'rewards',
  'postcode-iframe',
  'reservation',
  'bookings',
  'reserve-iframe',
  'new-reservation',
  'updateaccount',
  'delete-account',
  'forgot-password',
  'my-rewards',
  'previous-order',
  'checkout',
  'new-checkout',
  'order-checkout',
  'terms-and-conditions',
  'privacy-policy',
  'guest',
  'new-guest-checkout',
  'pay',
  'giftcard',
  'gift-cards',
  'branches',
  'online',
  'shop',
  'store',
  'distributors',
  'category-products',
  'refer',
  'favourites',
  'likeAndShare',
  'stock-list',
  'sold-cars',
  'invitation',
];

/**
 * Pages that moved to a new path. The old path still routes (a redirect in
 * its old module), but a config tile may still name it; headers put both
 * sides through movedPagePath() so the tab lights on the new page too.
 */
export const MOVED_PAGE_PATHS: { [oldPath: string]: string } = {
  'reservation/service-booking': 'bookings',
};

/** The current path of a page that may have moved (see MOVED_PAGE_PATHS). */
export function movedPagePath(path: string): string {
  return MOVED_PAGE_PATHS[path] || path;
}

export type BrandSource = 'path' | 'host' | 'referrer' | 'none';

interface BrandState {
  flavor: string | null;
  /** true when the current URL carries the flavour as its first segment */
  inPath: boolean;
  source: BrandSource;
}

const state: BrandState = { flavor: null, inPath: true, source: 'none' };

/** localStorage key (not brand-namespaced: it is read before the brand is known) */
const HOST_KEY_PREFIX = 'host-brand ';
/** re-check a cached host → brand answer in the background after this long */
const HOST_CACHE_TTL_MS = 60 * 60 * 1000;
/** how long a cold boot waits for ResolveHost before giving up */
const RESOLVE_TIMEOUT_MS = 6000;

/** The brand flavour of this page, or null when it could not be resolved. */
export function brandFlavor(): string | null {
  return state.flavor;
}

/** true when the brand came from the URL path (`/<flavor>/…`). */
export function brandInPath(): boolean {
  return state.inPath;
}

export function brandSource(): BrandSource {
  return state.source;
}

/** `/<flavor>/menu` → ['menu'] (or `/menu` → ['menu'] on a brand host). Query and hash dropped. */
function pathSegments(path: string): string[] {
  const clean = path.split('?')[0].split('#')[0];
  const segs = clean.split('/').filter(Boolean);
  return state.inPath ? segs.slice(1) : segs;
}

/** Path segments of the current page after the brand. */
export function pageSegments(): string[] {
  return pathSegments(window.location.pathname);
}

/**
 * The page segment (what used to be `location.pathname.split('/')[2]`):
 * 'menu', 'reservation', 'new-checkout' …; `pageSegment(1)` is the one after
 * it (`service-booking`, a table id). Empty string when absent.
 */
export function pageSegment(index = 0): string {
  return pageSegments()[index] || '';
}

/** Everything after the brand, joined: 'reservation/service-booking'. */
export function pagePath(): string {
  return pageSegments().join('/');
}

/** Segments after the brand of a router URL string (`event.url`, `state.url`). */
export function segmentsOf(url: string): string[] {
  return pathSegments(url || '');
}

/** The page segment of a router URL string (query string dropped). */
export function pageOf(url: string, index = 0): string {
  return segmentsOf(url)[index] || '';
}

/**
 * A page URL in the form the current page uses: 'primebarbers/menu' when the
 * brand is in the path, plain 'menu' on a brand host. `page` may carry a
 * query string ('login?guest=false') or child segments.
 */
export function brandUrl(page: string): string {
  const clean = (page || '').replace(/^\/+/, '');
  return state.inPath && state.flavor ? `${state.flavor}/${clean}` : clean;
}

/** Everything a brand URL needs: 'https://host/primebarbers/menu' or 'https://host/menu'. */
export function absoluteBrandUrl(page: string): string {
  return `${window.location.protocol}//${window.location.host}/${brandUrl(page)}`;
}

/** Is `segment` one of the app's own top-level pages? */
export function isRouteRoot(segment: string): boolean {
  return ROUTE_ROOTS.indexOf(segment) >= 0;
}

/**
 * Boot check for app-routing.module.ts: every top-level route's first
 * segment must be in ROUTE_ROOTS, otherwise a brand-less URL for that page
 * would be read as a brand flavour. Throws outside production builds.
 */
export function assertRouteRootsKnown(paths: string[]): void {
  const missing = paths
    .map((p) => p.split('/')[0])
    .filter((root) => root && root[0] !== ':' && root !== '**' && !isRouteRoot(root));
  if (missing.length === 0) return;
  const msg = `brand-context: add these route roots to ROUTE_ROOTS: ${missing.join(', ')}`;
  if (environment.production) console.error(msg);
  else throw new Error(msg);
}

/** 'https://WWW.Example.co.uk:443/x' or 'www.example.co.uk' → 'example.co.uk' */
export function normalizeHost(value: string): string {
  let s = (value || '').trim().toLowerCase();
  const scheme = s.indexOf('://');
  if (scheme >= 0) s = s.substring(scheme + 3);
  const end = s.search(/[/?#]/);
  if (end >= 0) s = s.substring(0, end);
  const colon = s.indexOf(':');
  if (colon >= 0) s = s.substring(0, colon);
  if (s.indexOf('www.') === 0) s = s.substring(4);
  return s.replace(/\.+$/, '');
}

interface HostCache {
  f: string;
  t: number;
}

function readHostCache(host: string): HostCache | null {
  try {
    const raw = localStorage.getItem(HOST_KEY_PREFIX + host);
    const d = raw ? (JSON.parse(raw) as HostCache) : null;
    return d && typeof d.f === 'string' && d.f ? d : null;
  } catch {
    return null;
  }
}

function writeHostCache(host: string, flavor: string): void {
  try {
    localStorage.setItem(HOST_KEY_PREFIX + host, JSON.stringify({ f: flavor, t: Date.now() }));
  } catch {
    /* storage blocked: resolve again next time */
  }
}

/** api/Brand/ResolveHost — the flavour the API maps this host to, or null. Never throws. */
async function resolveHostOnApi(host: string): Promise<string | null> {
  if (!host) return null;
  const controller = typeof AbortController === 'function' ? new AbortController() : null;
  const timer = controller ? setTimeout(() => controller.abort(), RESOLVE_TIMEOUT_MS) : null;
  try {
    const res = await fetch(
      `${environment.apiUrl}api/Brand/ResolveHost?host=${encodeURIComponent(host)}`,
      { signal: controller?.signal, credentials: 'omit' }
    );
    const body = await res.json();
    const flavor = body?.Info?.Status == 200 ? body?.Data?.BrandFlavor : null;
    return typeof flavor === 'string' && flavor ? flavor : null;
  } catch {
    return null;
  } finally {
    if (timer) clearTimeout(timer);
  }
}

/** Cached answer first (refreshed in the background when stale), else the API. */
async function resolveHost(host: string): Promise<string | null> {
  if (!host) return null;
  const cached = readHostCache(host);
  if (cached) {
    if (Date.now() - cached.t > HOST_CACHE_TTL_MS) {
      resolveHostOnApi(host).then((fresh) => {
        if (fresh) writeHostCache(host, fresh);
      });
    }
    return cached.f;
  }
  const fresh = await resolveHostOnApi(host);
  if (fresh) writeHostCache(host, fresh);
  return fresh;
}

/** The embedding page's host when the portal runs inside an iframe, else ''. */
function referrerHost(): string {
  try {
    if (window.self === window.top) return '';
  } catch {
    /* cross-origin top: we are framed */
  }
  return normalizeHost(document.referrer);
}

/**
 * Resolves the brand for this page load (see the module comment). Run from
 * APP_INITIALIZER so routing, the HTTP interceptor and storage namespacing
 * all see the answer. Never rejects: an unresolved brand leaves `flavor`
 * null, and ApiService reports "Brand Flavor Missing" as it always has.
 */
export async function resolveBrand(): Promise<void> {
  const first = window.location.pathname.split('/').filter(Boolean)[0] || '';

  if (first && !isRouteRoot(first)) {
    state.flavor = decodeURIComponent(first);
    state.inPath = true;
    state.source = 'path';
    return;
  }

  state.inPath = false;

  const host = normalizeHost(window.location.hostname);
  const byHost = await resolveHost(host);
  if (byHost) {
    state.flavor = byHost;
    state.source = 'host';
    return;
  }

  const parent = referrerHost();
  const byReferrer = parent && parent !== host ? await resolveHost(parent) : null;
  if (byReferrer) {
    state.flavor = byReferrer;
    state.source = 'referrer';
    return;
  }

  state.flavor = null;
  state.source = 'none';
}

/** Test seam: set the resolved brand directly (specs only). */
export function setBrandForTests(flavor: string | null, inPath: boolean): void {
  state.flavor = flavor;
  state.inPath = inPath;
  state.source = flavor ? (inPath ? 'path' : 'host') : 'none';
}

/** Injectable wrapper over the helpers, for code that prefers DI. */
@Injectable({ providedIn: 'root' })
export class BrandContextService {
  get flavor(): string | null {
    return brandFlavor();
  }
  get inPath(): boolean {
    return brandInPath();
  }
  get source(): BrandSource {
    return brandSource();
  }
  page(index = 0): string {
    return pageSegment(index);
  }
  path(): string {
    return pagePath();
  }
  url(page: string): string {
    return brandUrl(page);
  }
}
